import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { User } from "pwapi";

import { logout } from "shared/actions/authActions";
import { wipeCaseDetail } from "shared/actions/caseDetailActions";
import { wipeCases } from "shared/actions/casesActions";
import { wipeSlideDetail } from "shared/actions/slideDetailActions";
import {
  useCanSeeFrontEndAdmin,
  useCanSeeLIS,
} from "shared/hooks/usePermissions";

import ImpersonationLogOutButton from "components/ImpersonationLogOutButton";
import LinkButton from "components/LinkButton";
import ApplicationState from "store/ApplicationState";

import "./ProfileMenu.css";

function getInitials(first?: string, last?: string, username?: string) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  if (f || l) return (f[0] || "") + (l[0] || "");
  return (username || "?").slice(0, 2);
}

const ProfileMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );
  const hasImpersonator: boolean = useSelector(
    ({ auth }: ApplicationState) => !!auth.access?.impersonator_id
  );
  const canSeeAdmin = useCanSeeFrontEndAdmin();
  const canSeeLIS = useCanSeeLIS();

  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || btnRef.current?.contains(target))
        return;
      close();
    };

    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        btnRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handler);

    return () => document.removeEventListener("keydown", handler);
  }, [open, close]);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(wipeCases());
    dispatch(wipeCaseDetail());
    dispatch(wipeSlideDetail());
    close();
  };

  const handleSettings = () => {
    navigate("/settings");
    close();
  };

  if (!user) return null;

  const first = user.first_name;
  const last = user.last_name;
  const username = user.username || "";
  const fullName = `${user.first_name}\u00A0${user.last_name}`;
  const initials = getInitials(first, last, username).toUpperCase();

  return (
    <div className="ProfileMenu">
      <LinkButton
        ref={btnRef}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="ProfileMenu__trigger"
      >
        {user.first_name || user.last_name ? (
          <span>{fullName}</span>
        ) : (
          <span>{user.username}</span>
        )}
      </LinkButton>

      <div
        ref={menuRef}
        className={`ProfileMenu__menu ${open ? "ProfileMenu__menu--open" : ""}`}
        role="menu"
        aria-hidden={!open}
      >
        <header className="ProfileMenu__header" role="presentation">
          <span className="ProfileMenu__name">{fullName}</span>
          <span className="ProfileMenu__initials" aria-hidden="true">
            {initials}
          </span>
        </header>
        <hr className="ProfileMenu__rule" />
        <button
          type="button"
          role="menuitem"
          className="ProfileMenu__item"
          onClick={handleSettings}
        >
          Settings
        </button>
        {canSeeLIS && (
          <Link
            to={`${process.env.REACT_APP_BACKEND_PROTOCOL}://${process.env.REACT_APP_LIS_DOMAIN}`}
            target="_blank"
            className="ProfileMenu__item"
            rel="noopener noreferrer"
            aria-label="LIS (opens in a new window)"
            role="menuitem"
          >
            LIS
          </Link>
        )}
        {canSeeAdmin && (
          <Link
            to="/admin"
            target="_blank"
            className="ProfileMenu__item"
            rel="noopener noreferrer"
            aria-label="Admin (opens in a new window)"
            role="menuitem"
          >
            Admin
          </Link>
        )}
        {hasImpersonator ? (
          <ImpersonationLogOutButton
            className="ProfileMenu__item ProfileMenu__item--danger"
            type="link"
          />
        ) : (
          <button
            type="button"
            role="menuitem"
            className="ProfileMenu__item ProfileMenu__item--danger"
            onClick={handleLogout}
          >
            Log Out
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileMenu;
